/**
 * Support-ticket hooks (open board + admin triage).
 *
 *   GET   /tickets               useTickets({status,type,mine})
 *   GET   /tickets/{id}          useTicket(id)
 *   POST  /tickets               useSubmitTicket
 *   POST  /tickets/{id}/comments useAddTicketComment(id)
 *   PATCH /tickets/{id}          useUpdateTicket(id)   (admin)
 *   GET   /tickets/assignees     useTicketAssignees()  (admin)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type { MentionableUser } from './useMentionableUsers'
import type {
  Ticket,
  TicketCreateInput,
  TicketDetail,
  TicketListResponse,
  TicketUpdateInput,
} from '../types/ticket'

interface TicketFilters {
  status?: string
  type?: string
  mine?: boolean
}

export function useTickets({ status, type, mine }: TicketFilters = {}) {
  return useQuery<TicketListResponse>({
    queryKey: ['tickets', { status: status || '', type: type || '', mine: !!mine }],
    queryFn: async () => {
      const params: Record<string, string | boolean> = {}
      if (status) params.status = status
      if (type) params.type = type
      if (mine) params.mine = true
      const res = await api.get('/tickets', { params })
      return res.data
    },
  })
}

export function useTicket(id: number | null) {
  return useQuery<TicketDetail>({
    queryKey: ['tickets', 'detail', id],
    enabled: id != null,
    queryFn: async () => (await api.get(`/tickets/${id}`)).data,
  })
}

export function useSubmitTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: TicketCreateInput) =>
      (await api.post('/tickets', input)).data as Ticket,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  })
}

export function useAddTicketComment(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { body: string; is_internal?: boolean }) =>
      (await api.post(`/tickets/${id}/comments`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets', 'detail', id] }),
  })
}

export function useUpdateTicket(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: TicketUpdateInput) =>
      (await api.patch(`/tickets/${id}`, patch)).data as Ticket,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
      qc.invalidateQueries({ queryKey: ['tickets', 'detail', id] })
    },
  })
}

/** Admin-only: the assignable maintainers (admins/super-admins) for triage,
 * MentionableUser shape (incl. role) for the assignee PersonPicker. */
export function useTicketAssignees(enabled = true) {
  return useQuery<MentionableUser[]>({
    queryKey: ['tickets', 'assignees'],
    enabled,
    queryFn: async () => (await api.get('/tickets/assignees')).data,
  })
}

/** Admin-only: all approved users (MentionableUser shape), for re-attributing a
 * ticket's submitter via the searchable PersonPicker. */
export function useTicketUsers(enabled = true) {
  return useQuery<MentionableUser[]>({
    queryKey: ['tickets', 'users'],
    enabled,
    queryFn: async () => (await api.get('/tickets/users')).data,
  })
}
